import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, TrendingUp, Package, ShoppingCart, ArrowRight, RefreshCw, Sparkles } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import api from '../api';
import StatusBadge from '../components/StatusBadge';

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [summary, setSummary] = useState('');
  const [summaryLoading, setSummaryLoading] = useState(false);
  const navigate = useNavigate();

  const loadDashboard = async () => {
    setLoading(true);
    try {
      const result = await api.dashboard();
      setData(result);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadDashboard(); }, []);

  const handleSeed = async () => {
    setSeeding(true);
    try {
      await api.seed();
      await loadDashboard();
    } catch (err) {
      console.error(err);
    } finally {
      setSeeding(false);
    }
  };

  const loadSummary = async () => {
    setSummaryLoading(true);
    try {
      const result = await api.executiveSummary();
      setSummary(result.summary || '');
    } catch (err) {
      setSummary('No se pudo generar el resumen.');
    } finally {
      setSummaryLoading(false);
    }
  };

  if (loading) return <div className="loading-spinner"><div className="spinner" /></div>;

  if (!data?.has_data) {
    return (
      <div>
        <div className="section-card" style={{textAlign:'center',padding:'48px 24px'}}>
          <Package size={56} style={{color:'hsl(var(--primary))',margin:'0 auto 16px'}} />
          <h2 style={{fontSize:'1.3rem',marginBottom:'8px'}}>Bienvenido a LubriStock</h2>
          <p style={{color:'hsl(var(--muted-foreground))',fontSize:'0.9rem',maxWidth:'500px',margin:'0 auto 24px',lineHeight:'1.6'}}>
            Para comenzar, sigue estos pasos:
          </p>
          
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:'16px',maxWidth:'700px',margin:'0 auto 32px',textAlign:'left'}}>
            <div style={{padding:'16px',borderRadius:'10px',border:'1px solid hsl(var(--border))',background:'hsl(var(--card))'}}>
              <div style={{width:'28px',height:'28px',borderRadius:'50%',background:'hsl(var(--primary))',color:'white',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'0.8rem',fontWeight:700,marginBottom:'10px'}}>1</div>
              <h4 style={{fontSize:'0.85rem',marginBottom:'4px'}}>Sube tu catálogo</h4>
              <p style={{fontSize:'0.75rem',color:'hsl(var(--muted-foreground))',lineHeight:'1.4'}}>Ve a Catálogo y sube tu lista de productos (Excel/CSV)</p>
            </div>
            <div style={{padding:'16px',borderRadius:'10px',border:'1px solid hsl(var(--border))',background:'hsl(var(--card))'}}>
              <div style={{width:'28px',height:'28px',borderRadius:'50%',background:'hsl(var(--primary))',color:'white',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'0.8rem',fontWeight:700,marginBottom:'10px'}}>2</div>
              <h4 style={{fontSize:'0.85rem',marginBottom:'4px'}}>Sube tu inventario</h4>
              <p style={{fontSize:'0.75rem',color:'hsl(var(--muted-foreground))',lineHeight:'1.4'}}>Ve a Cargar archivos y sube el inventario actual</p>
            </div>
            <div style={{padding:'16px',borderRadius:'10px',border:'1px solid hsl(var(--border))',background:'hsl(var(--card))'}}>
              <div style={{width:'28px',height:'28px',borderRadius:'50%',background:'hsl(var(--primary))',color:'white',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'0.8rem',fontWeight:700,marginBottom:'10px'}}>3</div>
              <h4 style={{fontSize:'0.85rem',marginBottom:'4px'}}>Sube tus facturas</h4>
              <p style={{fontSize:'0.75rem',color:'hsl(var(--muted-foreground))',lineHeight:'1.4'}}>Sube las facturas de tus proveedores (PDF/Excel)</p>
            </div>
          </div>
          
          <div style={{display:'flex',gap:'12px',justifyContent:'center'}}>
            <button className="btn btn-primary" onClick={() => navigate('/catalog')} style={{height:'42px'}}>
              Ir al Catálogo
            </button>
            <button className="btn btn-secondary" onClick={handleSeed} disabled={seeding} data-testid="seed-button">
              {seeding ? 'Generando...' : 'Cargar datos de ejemplo'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* KPI Cards */}
      <div className="kpi-grid">
        <div className="kpi-card" data-testid="dashboard-kpi-coverage">
          <div className="kpi-label">Cobertura Promedio</div>
          <div className={`kpi-value ${data.avg_coverage < 14 ? 'danger' : data.avg_coverage < 21 ? 'warning' : ''}`}>
            {data.avg_coverage?.toFixed(0)}<span style={{fontSize:'0.9rem',fontWeight:400}}> días</span>
          </div>
          <div className="kpi-change">Meta: 21-30 días</div>
        </div>
        <div className="kpi-card" data-testid="dashboard-kpi-critical-products">
          <div className="kpi-label">Productos Críticos</div>
          <div className={`kpi-value ${data.critical_products > 0 ? 'danger' : 'success'}`}>
            {data.critical_products}
          </div>
          <div className="kpi-change">Riesgo de quiebre</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label">Inventario Neto</div>
          <div className="kpi-value">{data.net_inventory?.toLocaleString()}</div>
          <div className="kpi-change">{data.total_products} productos activos</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label">Unidades Sugeridas</div>
          <div className="kpi-value">{data.total_suggested?.toLocaleString()}</div>
          <div className="kpi-change">
            <button className="btn btn-sm btn-primary" onClick={() => navigate('/orders')} style={{marginTop:'4px'}}>
              Ver pedido <ArrowRight size={12} />
            </button>
          </div>
        </div>
      </div>

      {/* AI Summary */}
      <div className="section-card" style={{marginBottom:'20px'}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'10px'}}>
          <h3 style={{display:'flex',alignItems:'center',gap:'8px'}}><Sparkles size={16} style={{color:'hsl(199,84%,38%)'}} /> Resumen Ejecutivo IA</h3>
          <button className="btn btn-sm btn-secondary" onClick={loadSummary} disabled={summaryLoading}>
            {summaryLoading ? 'Generando...' : 'Generar resumen'}
          </button>
        </div>
        {summary ? (
          <p style={{fontSize:'0.85rem',lineHeight:'1.6',color:'hsl(222,22%,25%)'}}>{summary}</p>
        ) : (
          <p style={{fontSize:'0.82rem',color:'hsl(var(--muted-foreground))'}}>Haz clic en "Generar resumen" para obtener un análisis ejecutivo asistido por IA.</p>
        )}
      </div>

      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'20px'}}>
        {/* Critical Products */}
        <div className="data-table-container" data-testid="dashboard-critical-products-table">
          <div className="data-table-header">
            <h3 style={{display:'flex',alignItems:'center',gap:'8px'}}><AlertTriangle size={16} style={{color:'hsl(0,72%,48%)'}} /> Productos Críticos</h3>
            <button className="btn btn-sm btn-secondary" onClick={() => navigate('/orders')}>Ver todos</button>
          </div>
          <table className="data-table">
            <thead>
              <tr>
                <th>Producto</th>
                <th className="right">Stock Neto</th>
                <th className="right">Cobertura</th>
                <th className="right">Sugerido</th>
              </tr>
            </thead>
            <tbody>
              {data.alerts?.slice(0, 8).map((item, i) => (
                <tr key={i}>
                  <td>
                    <div style={{fontWeight:500,fontSize:'0.8rem'}}>{item.name}</div>
                    <div style={{fontSize:'0.7rem',color:'hsl(var(--muted-foreground))'}}>{item.brand} | {item.sku}</div>
                  </td>
                  <td className="right mono">{item.stock_neto}</td>
                  <td className="right">
                    <StatusBadge status="CRITICO" label={`${item.coverage_days?.toFixed(0)}d`} />
                  </td>
                  <td className="right mono" style={{fontWeight:600}}>{item.pedido_sugerido}</td>
                </tr>
              ))}
              {(!data.alerts || data.alerts.length === 0) && (
                <tr><td colSpan={4} style={{textAlign:'center',padding:'20px',color:'hsl(var(--muted-foreground))'}}>Sin productos críticos</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Brand Summary */}
        <div className="data-table-container">
          <div className="data-table-header">
            <h3 style={{display:'flex',alignItems:'center',gap:'8px'}}><TrendingUp size={16} style={{color:'hsl(199,84%,38%)'}} /> Resumen por Marca</h3>
          </div>
          {data.brand_summary && (
            <div style={{padding:'16px'}}>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={data.brand_summary}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(214,18%,88%)" />
                  <XAxis dataKey="brand" tick={{fontSize:11}} angle={-25} textAnchor="end" height={60} />
                  <YAxis tick={{fontSize:11}} />
                  <Tooltip contentStyle={{fontSize:'0.78rem',borderRadius:'8px',border:'1px solid hsl(214,18%,88%)'}} />
                  <Bar dataKey="total_stock" fill="hsl(199,84%,38%)" radius={[4,4,0,0]} name="Stock" />
                  <Bar dataKey="critical" fill="hsl(0,72%,48%)" radius={[4,4,0,0]} name="Críticos" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div style={{display:'flex',gap:'12px',marginTop:'20px',flexWrap:'wrap'}}>
        <button className="btn btn-secondary" onClick={handleSeed} disabled={seeding} data-testid="reseed-button">
          <RefreshCw size={14} /> {seeding ? 'Regenerando...' : 'Regenerar datos ejemplo'}
        </button>
        <button className="btn btn-primary" onClick={() => navigate('/upload')}>
          <TrendingUp size={14} /> Cargar archivo
        </button>
        <button className="btn btn-secondary" onClick={() => navigate('/orders')}>
          <ShoppingCart size={14} /> Ver pedido sugerido
        </button>
      </div>
    </div>
  );
}
